export type AdvancedCanvasNestedTarget<TPlacement = unknown> = {
  childIndex: number;
  gridPlacement?: TPlacement;
  legacy: boolean;
  parent?: AdvancedCanvasNestedTarget<TPlacement>;
  slotKey: string;
};

export function advancedCanvasNestedTargetFromPart<TPlacement = unknown>(
  part: string | null,
): AdvancedCanvasNestedTarget<TPlacement> | null {
  if (!part) {
    return null;
  }

  const values = part.split(":");

  function withOuterParent(
    target: AdvancedCanvasNestedTarget<TPlacement>,
    parent: AdvancedCanvasNestedTarget<TPlacement>,
  ): AdvancedCanvasNestedTarget<TPlacement> {
    return target.parent
      ? {
          ...target,
          parent: withOuterParent(target.parent, parent),
        }
      : {
          ...target,
          parent,
        };
  }

  function parseAt(
    offset: number,
  ): AdvancedCanvasNestedTarget<TPlacement> | null {
    const kind = values[offset];

    if (kind === "child") {
      const index = Number(values[offset + 1] ?? "");
      const target = Number.isInteger(index) && index >= 0
        ? {
            childIndex: index,
            legacy: true,
            slotKey: "children",
          } satisfies AdvancedCanvasNestedTarget<TPlacement>
        : null;

      if (!target) {
        return null;
      }

      const child = values.length > offset + 2
        ? parseAt(offset + 2)
        : null;

      return child ? withOuterParent(child, target) : target;
    }

    if (kind === "slot") {
      const slotKey = values[offset + 1] ?? "";
      const index = Number(values[offset + 2] ?? "");
      const target = slotKey !== ""
        && Number.isInteger(index)
        && index >= 0
        ? {
            childIndex: index,
            legacy: false,
            slotKey,
          } satisfies AdvancedCanvasNestedTarget<TPlacement>
        : null;

      if (!target) {
        return null;
      }

      const child = values.length > offset + 3
        ? parseAt(offset + 3)
        : null;

      return child ? withOuterParent(child, target) : target;
    }

    return null;
  }

  return parseAt(0);
}

export function advancedCanvasPartFromNestedTarget<TPlacement>(
  target: AdvancedCanvasNestedTarget<TPlacement>,
  childIndex = target.childIndex,
): string {
  const part = target.legacy
    ? `child:${childIndex}`
    : `slot:${target.slotKey}:${childIndex}`;

  return target.parent
    ? `${advancedCanvasPartFromNestedTarget(target.parent)}:${part}`
    : part;
}

export function advancedCanvasNestedTargetPath<TPlacement>(
  target: AdvancedCanvasNestedTarget<TPlacement>,
): string {
  const part = target.legacy
    ? `child:${target.childIndex}`
    : `slot:${target.slotKey}:${target.childIndex}`;

  return target.parent
    ? `${advancedCanvasNestedTargetPath(target.parent)}>${part}`
    : part;
}

export function advancedCanvasNestedContainerKey<TPlacement>(
  target: AdvancedCanvasNestedTarget<TPlacement>,
): string {
  const part = target.legacy ? "child" : `slot:${target.slotKey}`;

  return target.parent
    ? `${advancedCanvasNestedTargetPath(target.parent)}>${part}`
    : part;
}

export function advancedCanvasNestedContainersEqual<TPlacement>(
  left: AdvancedCanvasNestedTarget<TPlacement>,
  right: AdvancedCanvasNestedTarget<TPlacement>,
): boolean {
  return advancedCanvasNestedContainerKey(left)
    === advancedCanvasNestedContainerKey(right);
}
